import React, { useCallback, useEffect, useState } from 'react';
import { Box, Button, InputAdornment, Paper, Stack, TextField } from '@mui/material';
import { Search } from '@mui/icons-material';
import financeService from '../../../services/financeService';
import { CashBankResponse, JOURNAL_SOURCE_OPTIONS } from '../../../types/finance';
import { useDebounce } from '../../../hooks/useDebounce';
import DateRangePicker, { DateRange } from '../../common/DateRangePicker';
import SearchableSelect from '../../common/SearchableSelect';
import DownloadMenu from '../../common/DownloadMenu';
import JournalEntryDrawer from '../journal/JournalEntryDrawer';
import CashBankAccounts from './CashBankAccounts';
import CashMovementsTable from './CashMovementsTable';
import { format } from 'date-fns';
import { useAuth } from '../../../context/AuthContext';
import { usePdfExport, useSchoolBranding } from '../../../utils/pdf';
import CashBankDocument from '../pdf/CashBankDocument';

const DIRECTIONS = [{ value: 'in', label: 'Money in' }, { value: 'out', label: 'Money out' }];

/** Every rupee in and out of the school's cash and bank accounts, read from the books — each line opens its voucher. */
const CashMovementsPanel: React.FC = () => {
  const [range, setRange] = useState<DateRange>({ from: null, to: null });
  const [accountId, setAccountId] = useState('');
  const [direction, setDirection] = useState<'in' | 'out' | ''>('');
  const [sourceType, setSourceType] = useState('');
  const [q, setQ] = useState('');
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(25);
  const [data, setData] = useState<CashBankResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [openEntryId, setOpenEntryId] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const search = useDebounce(q, 300);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setData(await financeService.getCashBank({
        from: range.from, to: range.to, accountId, direction, sourceType, q: search, page: page + 1, limit,
      }));
      setError(null);
    } catch (e: any) {
      setError(e?.message || 'Could not load cash & bank.');
    } finally {
      setLoading(false);
    }
  }, [range, accountId, direction, sourceType, search, page, limit]);

  useEffect(() => { load(); }, [load, refreshKey]);
  useEffect(() => { setPage(0); }, [range, accountId, direction, sourceType, search]);

  const filtered = !!(range.from || range.to || accountId || direction || sourceType || q);
  const reset = () => { setRange({ from: null, to: null }); setAccountId(''); setDirection(''); setSourceType(''); setQ(''); };

  // The PDF is drawn here from every movement the filters match; the CSV comes from the server.
  const { currency } = useAuth();
  const { generate: generatePdf } = usePdfExport();
  const { branding } = useSchoolBranding();
  const exportAs = async (kind: 'pdf' | 'csv') => {
    const f = { from: range.from, to: range.to, accountId, direction, sourceType, q: search };
    if (kind === 'csv') return financeService.exportCashBankCsv(f);
    await generatePdf(async () => (
      <CashBankDocument report={await financeService.getCashBankReport(f)} currency={currency || 'PKR'} branding={branding} generatedAt={new Date().toISOString()} />
    ), `Cash_and_Bank_${format(new Date(), 'yyyy-MM-dd')}.pdf`);
  };

  return (
    <Box>
      {data && <CashBankAccounts accounts={data.accounts} selected={accountId} onSelect={setAccountId} />}

      <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, mb: 2 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ xs: 'stretch', md: 'center' }}>
          <DateRangePicker value={range} onChange={setRange} label="Dates" minWidth={220} />
          <SearchableSelect label="Direction" value={direction} onChange={v => setDirection(v as 'in' | 'out' | '')} options={DIRECTIONS} placeholder="In and out" minWidth={160} />
          <SearchableSelect label="Source" value={sourceType} onChange={setSourceType} options={JOURNAL_SOURCE_OPTIONS} placeholder="All sources" minWidth={190} />
          <TextField
            size="small"
            placeholder="Search voucher or description…"
            value={q}
            onChange={e => setQ(e.target.value)}
            sx={{ flex: 1, minWidth: 200 }}
            InputProps={{ startAdornment: <InputAdornment position="start"><Search fontSize="small" color="action" /></InputAdornment> }}
          />
          {filtered && <Button size="small" onClick={reset}>Reset</Button>}
          <DownloadMenu onExport={exportAs} />
        </Stack>
      </Paper>

      <CashMovementsTable
        data={data}
        loading={loading}
        error={error}
        filtered={filtered}
        page={page}
        limit={limit}
        onPage={setPage}
        onLimit={l => { setLimit(l); setPage(0); }}
        onOpen={setOpenEntryId}
      />

      <JournalEntryDrawer entryId={openEntryId} onClose={() => setOpenEntryId(null)} onChanged={() => setRefreshKey(k => k + 1)} />
    </Box>
  );
};

export default CashMovementsPanel;
